import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useWeeklyProgress } from '@/lib/queries';

export function GoalTrend({ goalId }: { goalId: string }) {
  const { t, i18n } = useTranslation();
  const query = useWeeklyProgress(goalId);
  const titleId = useId();
  const points = query.data ?? [];
  const date = (value: string) =>
    new Intl.DateTimeFormat(i18n.language, {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    }).format(new Date(`${value}T00:00:00Z`));
  const first = Date.parse(points[0]?.weekStart ?? '2026-01-01');
  const last = Date.parse(points.at(-1)?.weekStart ?? '2026-01-01');
  const x = (value: string) =>
    first === last ? 240 : 44 + ((Date.parse(value) - first) / (last - first)) * 406;
  const y = (value: number) => 174 - value * 1.4;
  return (
    <section className="min-w-0 rounded-card border border-line bg-surface p-4">
      <h2 className="font-medium">{t('trend.title')}</h2>
      <p className="mt-1 text-sm text-muted">{t('trend.hint')}</p>
      {query.isError ? (
        <p role="alert">{t('errors.loadFailed')}</p>
      ) : query.isPending ? (
        <p role="status">{t('common.loading')}</p>
      ) : !points.length ? (
        <p className="mt-4 text-sm">{t('trend.empty')}</p>
      ) : (
        <>
          <svg
            viewBox="0 0 480 210"
            className="my-3 w-full text-accent"
            role="img"
            aria-labelledby={titleId}
          >
            <title id={titleId}>{t('trend.title')}</title>
            {[0, 50, 100].map((v) => (
              <g key={v}>
                <line x1="44" x2="450" y1={y(v)} y2={y(v)} className="stroke-line" />
                <text x="4" y={y(v) + 4} fontSize="12" className="fill-muted">
                  {new Intl.NumberFormat(i18n.language, { style: 'percent' }).format(v / 100)}
                </text>
              </g>
            ))}
            {points.map((point, i) => {
              const previous = points[i - 1];
              if (point.progressPercent === null) return null;
              const connected =
                previous &&
                previous.progressPercent !== null &&
                previous.progressMode === point.progressMode &&
                Date.parse(point.weekStart) - Date.parse(previous.weekStart) === 7 * 86400000;
              return (
                <g key={point.weekStart}>
                  {connected && (
                    <line
                      x1={x(previous.weekStart)}
                      y1={y(previous.progressPercent!)}
                      x2={x(point.weekStart)}
                      y2={y(point.progressPercent)}
                      stroke="currentColor"
                      strokeWidth="3"
                    />
                  )}
                  <circle
                    cx={x(point.weekStart)}
                    cy={y(point.progressPercent)}
                    r="4"
                    fill="currentColor"
                  />
                </g>
              );
            })}
            <text x="44" y="202" fontSize="12" className="fill-muted">
              {date(points[0]!.capturedOn)}
            </text>
            {points.length > 1 && (
              <text x="450" y="202" textAnchor="end" fontSize="12" className="fill-muted">
                {date(points.at(-1)!.capturedOn)}
              </text>
            )}
          </svg>
          <details>
            <summary className="min-h-11 cursor-pointer py-2 text-sm">{t('trend.values')}</summary>
            <ul className="flex flex-col gap-2 text-sm">
              {points.map((point) => (
                <li className="flex flex-wrap justify-between gap-2" key={point.weekStart}>
                  <span>
                    {date(point.capturedOn)} · {t(`goal.mode.${point.progressMode}`)}
                  </span>
                  <span>
                    {point.progressPercent === null
                      ? t('goal.pace.noData')
                      : new Intl.NumberFormat(i18n.language, {
                          style: 'percent',
                          maximumFractionDigits: 1,
                        }).format(point.progressPercent / 100)}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        </>
      )}
    </section>
  );
}
